/* eslint-disable max-lines-per-function */
import { Filter, X } from "lucide-react";
import { useMemo, useState, type ReactElement } from "react";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../../../shared/components/ui/select";
import { Slider } from "../../../../shared/components/ui/slider";
import type { BulkEditTitle, FilterState } from "../types";

interface FilterBarProps {
  readonly titles: BulkEditTitle[];
  readonly filterState: FilterState;
  readonly onFilterChange: (updates: Partial<FilterState>) => void;
  readonly onClearFilters: () => void;
  readonly hasActiveFilters: boolean;
}

/**
 * Filter bar for bulk title editor
 * Provides status, genre, and year range filtering with URL param sync
 */
export function FilterBar({
  titles,
  filterState,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
}: Readonly<FilterBarProps>): ReactElement {
  const [showGenreDropdown, setShowGenreDropdown] = useState(false);

  // Get unique genres from all titles
  const availableGenres = useMemo(() => {
    const genreSet = new Set<string>();
    for (const title of titles) {
      for (const genre of title.genres) {
        genreSet.add(genre);
      }
    }
    return Array.from(genreSet).sort((a, b) => a.localeCompare(b));
  }, [titles]);

  // Get year range from titles
  const yearBounds = useMemo(() => {
    const years = titles.map((t) => t.year).filter((y): y is number => y !== null && y !== undefined);
    if (years.length === 0) return { min: 1900, max: new Date().getFullYear() };
    return {
      min: Math.min(...years),
      max: Math.max(...years),
    };
  }, [titles]);

  const handleStatusChange = (value: string) => {
    onFilterChange({ status: value as "all" | "published" | "draft" });
  };

  const handleGenreToggle = (genre: string) => {
    const currentGenres = filterState.genres;
    const newGenres = currentGenres.includes(genre)
      ? currentGenres.filter((g) => g !== genre)
      : [...currentGenres, genre];
    onFilterChange({ genres: newGenres });
  };

  const handleYearRangeChange = (values: number[]) => {
    if (values.length === 2) {
      onFilterChange({ yearRange: [values[0], values[1]] });
    }
  };

  return (
    <div className="flex items-center gap-6 p-3 bg-neutral-900/30 border border-neutral-800 rounded-lg">
      <div className="flex items-center gap-2 text-sm font-medium text-white shrink-0">
        <Filter className={hasActiveFilters ? "h-4 w-4 text-lumi-accent" : "h-4 w-4"} />
        <span className={hasActiveFilters ? "text-lumi-accent" : ""}>Filters:</span>
      </div>

      <div className="flex flex-1 items-center gap-6">
        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <span
            className={
              filterState.status === "all" ? "text-xs text-neutral-400 shrink-0" : "text-xs text-lumi-accent shrink-0"
            }
          >
            Status:
          </span>
          <Select value={filterState.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[120px] h-8">
              <span className={filterState.status === "all" ? "text-sm" : "text-sm text-lumi-accent"}>
                {filterState.status === "all" && "All"}
                {filterState.status === "published" && "Published"}
                {filterState.status === "draft" && "Draft"}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Genre Filter */}
        <div className="flex items-center gap-2 relative">
          <span
            className={
              filterState.genres.length > 0 ? "text-xs text-lumi-accent shrink-0" : "text-xs text-neutral-400 shrink-0"
            }
          >
            Genres:
          </span>
          <button
            type="button"
            onClick={() => setShowGenreDropdown(!showGenreDropdown)}
            className="inline-flex h-8 w-[140px] items-center justify-between rounded-md border border-neutral-700/80 bg-neutral-900/40 px-3 py-2 text-sm text-white shadow-sm hover:bg-neutral-900/60 focus:outline-none focus:ring-2 focus:ring-neutral-400"
          >
            <span className={filterState.genres.length > 0 ? "text-sm truncate text-lumi-accent" : "text-sm truncate"}>
              {filterState.genres.length === 0 ? "All" : `${filterState.genres.length} selected`}
            </span>
            <span className="text-neutral-500">▼</span>
          </button>

          {showGenreDropdown && (
            <>
              {/* Backdrop */}
              <button
                type="button"
                className="fixed inset-0 z-40 bg-transparent"
                onClick={() => setShowGenreDropdown(false)}
                aria-label="Close genre filter"
              />

              {/* Dropdown */}
              <div className="absolute top-full left-0 mt-1 z-50 w-[240px] max-h-[300px] overflow-y-auto rounded-md border border-neutral-800 bg-neutral-950 p-2 shadow-xl">
                {availableGenres.length === 0 ? (
                  <div className="px-2 py-4 text-sm text-neutral-500 text-center">No genres available</div>
                ) : (
                  <div className="space-y-1">
                    {availableGenres.map((genre) => (
                      <label
                        key={genre}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-neutral-800 cursor-pointer"
                      >
                        <Checkbox
                          checked={filterState.genres.includes(genre)}
                          onCheckedChange={() => handleGenreToggle(genre)}
                        />
                        <span className="text-sm text-white">{genre}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Year Range Filter */}
        <div className="flex items-center gap-3 min-w-[320px]">
          <span
            className={
              filterState.yearRange[0] !== yearBounds.min || filterState.yearRange[1] !== yearBounds.max
                ? "text-xs text-lumi-accent shrink-0"
                : "text-xs text-neutral-400 shrink-0"
            }
          >
            Year:
          </span>
          <Slider
            min={yearBounds.min}
            max={yearBounds.max}
            step={1}
            value={filterState.yearRange}
            onValueChange={handleYearRangeChange}
            className="flex-1"
          />
          <span
            className={
              filterState.yearRange[0] !== yearBounds.min || filterState.yearRange[1] !== yearBounds.max
                ? "text-xs text-lumi-accent shrink-0 min-w-[70px] text-right"
                : "text-xs text-neutral-400 shrink-0 min-w-[70px] text-right"
            }
          >
            {filterState.yearRange[0]} - {filterState.yearRange[1]}
          </span>
        </div>
      </div>

      {/* Clear All Button */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          className="flex items-center gap-1 px-3 py-1.5 text-xs text-neutral-400 hover:text-white hover:bg-neutral-800 rounded transition-colors shrink-0"
        >
          <X className="h-3 w-3" />
          <span>Clear</span>
        </button>
      )}
    </div>
  );
}
